import { dispositionFileName } from '@nyce/admin-kit/api';
import { targetHubClient } from '../../identity/targetClient';

/**
 * nyce-logic-printqueue through the hub (`/printqueue/<target>/…`, `PrintQueue/PrintQueueEndpoints.cs`). The hub calls
 * the print queue with the operator's AUTH session of that environment when there is one (`X-Target-Token`), else with
 * its own Admin token, which the console never sees. Raw tokens are never listed either: `TokenInfo.id` is a hash the
 * hub resolves on delete. Tenants move to AUTH per `docs/printqueue-auth.md`.
 */

export type JobStatus = 'Registered' | 'Fetched' | 'Printing' | 'Printed' | 'Error';
export const JOB_STATUSES: JobStatus[] = ['Registered', 'Fetched', 'Printing', 'Printed', 'Error'];
/** Not finished yet: waiting for, or with, a print server. */
export const OPEN_STATUSES: JobStatus[] = ['Registered', 'Fetched', 'Printing'];

export interface PrintJob {
  id: number;
  tenantId: string;
  printServerName: string;
  printerName: string;
  printType: string;
  fileName?: string | null;
  copies: number;
  status: JobStatus;
  modificationDate?: string | null;
  dateAdded?: string | null;
  systemId?: string | null;
  /** Base64 print data: only on a single job (`job(id)`). */
  data?: string | null;
}

export interface Printer {
  id: number;
  name: string;
  printServerName: string;
  tenantId: string;
  dateAdded?: string | null;
}

/** `live` = connected to the print queue's hub, `polling` = calls `next` over HTTP, else `offline` (2 min window). */
export type PrintServerState = 'live' | 'polling' | 'offline';

/** A print server (tenant + name) with its last contact; print queue 1.5.0+ (`GET printservers`). */
export interface PrintServer {
  tenantId: string;
  name: string;
  state: PrintServerState;
  /** Latest contact of any kind; null = not seen since the print queue started recording (1.5.0). */
  lastSeenAt: string | null;
  connectedAt: string | null;
  disconnectedAt: string | null;
  lastHubAt: string | null;
  lastPollAt: string | null;
  lastUploadAt: string | null;
  /** Last job it marked printed itself; print queue 1.5.1+ (absent before). */
  lastPrintedAt?: string | null;
  /** Its client (print server version) from the last connect or poll. */
  userAgent: string | null;
  /** Version it reported (print server 3.0+, print queue 1.8.0+); null/absent = unknown, older print server. */
  version?: string | null;
  /** Package platform it reported: win-x64, linux-x64, … */
  platform?: string | null;
  /** Version of the bootstrapper running it; null = not run by the bootstrapper. */
  bootstrapperVersion?: string | null;
  /** Run by the bootstrapper, so it can be updated from here. */
  updatable?: boolean;
  /** Version it was last asked to update to (print queue 1.8.0+); kept once done. */
  updateTargetVersion?: string | null;
  updateState?: PrintServerUpdateState | null;
  /** Why the update failed, or what it reported last. */
  updateMessage?: string | null;
  updateRequestedAt?: string | null;
  updateStateAt?: string | null;
  /** Its logs (print queue 1.12.0+, absent before): asked for, with the print server, uploaded or failed; null = none. */
  logsState?: PrintServerLogsState | null;
  /** Days of logs asked for. */
  logsDays?: number | null;
  /** Why fetching them failed ("no answer" after 30 min, or the print server's reason). */
  logsMessage?: string | null;
  logsRequestedAt?: string | null;
  logsStateAt?: string | null;
  /** Zip size in bytes, when ready. */
  logsSize?: number | null;
  /** When the zip was uploaded; the print queue deletes it 7 days later. */
  logsCreatedAt?: string | null;
  printers: number;
  /** Registered, Fetched or Printing. */
  openJobs: number;
  errorJobs: number;
}

export type PrintServerUpdateState =
  'requested' | 'sent' | 'downloading' | 'verifying' | 'draining' | 'restarting' | 'done' | 'failed';

/** requested → sent (to the print server) → ready (zip kept 7 days) | failed. */
export type PrintServerLogsState = 'requested' | 'sent' | 'ready' | 'failed';

/** Where a log line comes from: the print server, a print engine it ran (`[Print Process <job>]`), its bootstrapper, NLog's internal log, or an unknown file's folder. */
export type LogSource = 'printserver' | 'engine' | 'bootstrapper' | 'nlog' | (string & {});
export type LogLevel = 'trace' | 'debug' | 'info' | 'warning' | 'error' | 'critical';
export const LOG_LEVELS: LogLevel[] = ['trace', 'debug', 'info', 'warning', 'error', 'critical'];

export interface LogEntry {
  /** With the print server's offset; null for a line without a time. */
  time: string | null;
  source: LogSource;
  level: LogLevel;
  /** Several lines for a stack trace. */
  message: string;
  /** The print job of an engine line. */
  job: number | null;
  file: string;
}

/** A page of a print server's fetched logs, read by the hub (newest first), with what the whole zip holds. */
export interface LogEntriesPage {
  info: string | null;
  files: { path: string; size: number; entries: number }[];
  /** More than the hub reads (200 000 lines / 256 MB): the oldest are left out. */
  truncated: boolean;
  total: number;
  sources: { name: LogSource; count: number }[];
  levels: { name: LogLevel; count: number }[];
  matched: number;
  offset: number;
  entries: LogEntry[];
}

export interface LogEntriesQuery {
  /** The zip's `logsCreatedAt`: the hub keeps it parsed while it is the same. */
  v?: string | null;
  sources?: string[];
  levels?: string[];
  q?: string;
  job?: number;
  offset?: number;
  limit?: number;
}

/** Print server releases the print queue serves to print servers updating themselves (1.8.0+). */
export interface PrintServerReleases {
  /** False when the print queue has no UPDATES_SOURCE. */
  configured: boolean;
  /** Newest first. */
  releases: { version: string; date: string | null; prerelease: boolean }[];
}

/** How a tenant's print servers are kept up to date (print queue 1.8.0+). */
export interface PrintServerUpdatePolicy {
  tenantId: string;
  /** Off: only by hand. Auto: every updatable print server not on targetVersion, inside the window. */
  mode: 'Off' | 'Auto';
  /** `latest` (newest release, not a prerelease) or a version to keep them on. */
  targetVersion: string;
  /** "HH:mm" in timeZone; both null = any time. */
  windowStart: string | null;
  windowEnd: string | null;
  timeZone: string;
  /** Print servers updating at once. */
  maxParallel: number;
  updatedAt?: string | null;
}

export interface UpdatePrintServer {
  tenantId: string;
  name: string;
  /** Empty = the newest release (not a prerelease) for its platform. */
  version?: string;
  /** Also to its own version, or one that failed to start there before. */
  force?: boolean;
}

/** One print server of a bulk update: the version it is asked for, or why not (`code`). */
export interface PrintServerUpdateResult {
  name: string;
  version: string | null;
  code: string | null;
  error: string | null;
}

export interface RemovePrintServer {
  tenantId: string;
  name: string;
  /** Every job queued for it, too. */
  deleteJobs?: boolean;
  /** Its PrintServer tokens (same tenant and name). */
  revokeTokens?: boolean;
  /** Its AUTH client `printserver-<tenant>-<name>` (needs the AUTH session). */
  removeAuthClient?: boolean;
}

export interface RemovedPrintServer {
  printers: number;
  jobs: number;
  tokensRevoked: number;
  authClient: string | null;
  /** Steps that failed; the others were done. */
  problems: string[];
}

export interface TokenInfo {
  /** SHA-256 prefix of the token: what delete takes. */
  id: string;
  tenantId: string | null;
  name: string | null;
  role: string | null;
  expires: string | null;
  dateAdded: string | null;
  /** `…` + the last 6 characters. */
  hint: string;
}

export interface JobQuery {
  tenantId?: string;
  printServerName?: string;
  statuses?: JobStatus[];
  /** Print queue 1.10.0: exact type ignoring case, file name containing this ignoring case (older ones ignore both). */
  printType?: string;
  fileName?: string;
  /** 1–1000, default 100. */
  size?: number;
  /** Keyset paging: ids below this one (newest first) or above it (`sortAsc`). */
  lastPageId?: number;
  sortAsc?: boolean;
}

/** Counters of one day (`yyyy-MM-dd`) or month (`yyyy-MM`), tenant and (with `by: 'printserver'`) print server. */
export interface UsageRow {
  period: string;
  tenantId: string;
  printServerName: string | null;
  added: number;
  addedBytes: number;
  printed: number;
  printedBytes: number;
  failed: number;
}

export type UsageTotals = Omit<UsageRow, 'period' | 'tenantId' | 'printServerName'>;

/** `GET usage` (print queue 1.7.0): counters kept after the jobs are deleted, days in `timeZone`. */
export interface PrintUsage {
  timeZone: string;
  from: string;
  to: string;
  groupBy: 'day' | 'month';
  by: 'tenant' | 'printserver';
  rows: UsageRow[];
  totals: UsageTotals;
}

export interface UsageQuery {
  /** `yyyy-MM-dd`, inclusive; default 29 days before `to`. */
  from?: string;
  /** `yyyy-MM-dd`, inclusive; default today in the print queue's usage zone. */
  to?: string;
  tenantId?: string;
  groupBy?: 'day' | 'month';
  by?: 'tenant' | 'printserver';
}

export type TokenRole = 'WorkServer' | 'PrintServer';

/** A tenant's move to AUTH: `Legacy` (default) → `Both` → `Auth` (its print queue tokens are refused). */
export type TenantAuthMode = 'Legacy' | 'Both' | 'Auth';

export interface TenantAuth {
  tenantId: string;
  mode: TenantAuthMode;
  /** Last call with a print queue token (WorkServer or print server), at most every 5 minutes. */
  lastLegacyAt: string | null;
  /** Last call with an AUTH token. `Auth` needs one. */
  lastBearerAt: string | null;
  modifiedAt: string | null;
}

/** A print server's AUTH client, secret shown once. */
export interface PrintServerClient {
  authClientId: string;
  authClientName: string;
  clientSecret: string;
  environment: Record<string, string>;
}

const query = (q: Record<string, string | number | boolean | undefined>) => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '' && v !== false) s.set(k, String(v));
  const str = s.toString();
  return str ? `?${str}` : '';
};

export function printQueue(target: string) {
  const base = `/printqueue/${encodeURIComponent(target)}`;
  const client = targetHubClient(target);
  const tenant = (id: string) => encodeURIComponent(id);
  return {
    ping: () => client.get<{ ok: boolean; ms: number }>(`${base}/ping`),
    jobs: (q: JobQuery = {}) =>
      client.get<PrintJob[]>(
        `${base}/queue${query({
          tenantId: q.tenantId,
          printServerName: q.printServerName,
          statuses: q.statuses?.join(','),
          printType: q.printType,
          fileName: q.fileName,
          size: q.size,
          lastPageId: q.lastPageId,
          sortAsc: q.sortAsc,
        })}`
      ),
    job: (id: number) => client.get<PrintJob>(`${base}/queue/${id}`),
    /** An FR job rendered as PDF by the hub (FastReport); 422 `render_failed` when it cannot be. */
    jobPdf: async (id: number) =>
      new Uint8Array(
        await (
          await client.requestRaw('GET', `${base}/queue/${id}/preview`, undefined, {
            accept: 'application/pdf',
          })
        ).arrayBuffer()
      ),
    /** A RAW job holding ZPL as PDF, one page per label (the first 20), with how many labels it holds. */
    jobLabels: async (id: number) => {
      const res = await client.requestRaw('GET', `${base}/queue/${id}/preview`, undefined, {
        accept: 'application/pdf',
      });
      const pdf = new Uint8Array(await res.arrayBuffer());
      return { pdf, labels: Number(res.headers.get('X-Label-Count')) || null };
    },
    printers: (q: { tenantId?: string; printServerName?: string } = {}) =>
      client.get<Printer[]>(`${base}/printers${query(q)}`),
    /** Print servers with their state; 404 from a print queue before 1.5.0. */
    printServers: (tenantId?: string) =>
      client.get<PrintServer[]>(`${base}/printservers${query({ tenantId })}`),
    /** Printers and contact row (+ jobs, tokens, AUTH client as asked); an older print queue only loses the printers. */
    removePrintServer: (body: RemovePrintServer) =>
      client.post<RemovedPrintServer>(`${base}/printservers/remove`, body),
    /** Releases print servers can update to; 404 from a print queue before 1.8.0. */
    releases: () => client.get<PrintServerReleases>(`${base}/updates`),
    /** Asks one print server (3.0+, run by the bootstrapper) to update. */
    updatePrintServer: (body: UpdatePrintServer) =>
      client.post<{ result: { version: string } }>(`${base}/printservers/update`, body),
    /** Withdraws an update the print server has not taken yet. */
    cancelPrintServerUpdate: (body: { tenantId: string; name: string }) =>
      client.post<{ result: null }>(`${base}/printservers/update/cancel`, body),
    /**
     * Asks a print server (3.1.0+, print queue 1.12.0+) for its last `days` (1–14, default 2) of logs; a zip fetched
     * before is deleted. 409 `not_supported` (older print server), `not_supported_by_printqueue` (older print queue).
     */
    requestLogs: (body: { tenantId: string; name: string; days?: number }) =>
      client.post<{ result: { days: number } }>(`${base}/printservers/logs`, body),
    /** Withdraws a logs request, or deletes the fetched zip. */
    cancelLogs: (body: { tenantId: string; name: string }) =>
      client.post<{ result: null }>(`${base}/printservers/logs/cancel`, body),
    /** The fetched logs zip and its file name; 404 `no_logs`. */
    downloadLogs: async (tenantId: string, name: string): Promise<{ blob: Blob; fileName: string }> => {
      const r = await client.requestRaw(
        'GET',
        `${base}/printservers/logs${query({ tenantId, name })}`,
        undefined,
        {
          accept: 'application/zip',
        }
      );
      return {
        blob: await r.blob(),
        fileName: dispositionFileName(
          r.headers.get('Content-Disposition'),
          `printserver-logs-${tenantId}-${name}.zip`
        ),
      };
    },
    /** The fetched logs read by the hub: filtered, newest first, a page at a time; 404 `no_logs`. */
    logEntries: (tenantId: string, name: string, q: LogEntriesQuery = {}) =>
      client.get<LogEntriesPage>(
        `${base}/printservers/logs/entries${query({
          tenantId,
          name,
          v: q.v ?? undefined,
          source: q.sources?.length ? q.sources.join(',') : undefined,
          level: q.levels?.length ? q.levels.join(',') : undefined,
          q: q.q || undefined,
          job: q.job,
          offset: q.offset,
          limit: q.limit,
        })}`
      ),
    /** Asks several print servers of a tenant (all without names) to update. */
    updatePrintServers: (body: { tenantId: string; names?: string[]; version?: string; force?: boolean }) =>
      client.post<{ result: PrintServerUpdateResult[] }>(`${base}/printservers/update-many`, body),
    updatePolicy: (tenantId: string) =>
      client.get<PrintServerUpdatePolicy[]>(`${base}/printservers/update-policy${query({ tenantId })}`),
    setUpdatePolicy: (policy: PrintServerUpdatePolicy) =>
      client.post<{ result: PrintServerUpdatePolicy }>(`${base}/printservers/update-policy`, policy),
    /** Usage counters; 404 from a print queue before 1.7.0. */
    usage: (q: UsageQuery = {}) => client.get<PrintUsage>(`${base}/usage${query({ ...q })}`),
    tokens: (tenantId?: string) => client.get<TokenInfo[]>(`${base}/tokens${query({ tenantId })}`),
    setStatus: (id: number, status: JobStatus) =>
      client.post<{ result: number }>(`${base}/queue/${id}/status`, { status }),
    deleteJobs: (ids: number[]) => client.post<{ result: number }>(`${base}/queue/delete`, { ids }),
    deletePrinters: (ids: number[]) => client.post<{ result: number }>(`${base}/printers/delete`, { ids }),
    createToken: (body: { tenantId: string; name: string; role: TokenRole; expireDays: number }) =>
      client.post<{ token: string; info: TokenInfo }>(`${base}/tokens`, body),
    deleteTokens: (ids: string[]) => client.post<{ result: number }>(`${base}/tokens/delete`, { ids }),
    /** Every tenant with its AUTH mode; 404 from a print queue before 1.3.0. */
    tenantAuth: () => client.get<TenantAuth[]>(`${base}/tenants/auth`),
    setTenantAuthMode: (tenantId: string, mode: TenantAuthMode) =>
      client.put<{ result: TenantAuth }>(`${base}/tenants/${tenant(tenantId)}/auth-mode`, { mode }),
    /** Creates (or reuses) a print server's AUTH client in the tenant's aliased AUTH tenant (needs the AUTH session). */
    printServerClient: (tenantId: string, printServerName: string) =>
      client.post<PrintServerClient>(`${base}/tenants/${tenant(tenantId)}/printserver-client`, {
        printServerName,
      }),
  };
}

export type PrintQueueApi = ReturnType<typeof printQueue>;

/** All jobs matching `q`, newest first, following the keyset pages up to `max`. */
export async function allJobs(
  api: PrintQueueApi,
  q: JobQuery,
  max = 5000
): Promise<{ jobs: PrintJob[]; more: boolean }> {
  const jobs: PrintJob[] = [];
  let lastPageId: number | undefined;
  for (;;) {
    const page = await api.jobs({ ...q, size: 1000, lastPageId, sortAsc: false });
    jobs.push(...page);
    if (page.length < 1000) return { jobs, more: false };
    if (jobs.length >= max) return { jobs, more: true };
    lastPageId = page[page.length - 1].id;
  }
}
